import axios, { AxiosInstance } from "axios";
import {
  ExternalAuthTokenRequest,
  ExternalAuthTokenResponse,
  ExternalForm,
  ExternalFormSubmitResponse,
  ExternalSubmissionListItem,
  ExternalSubmissionDetail,
} from "../types";

/**
 * External API Service for Softana Ticket API Integration
 * 
 * This service handles communication with external ticketing system.
 * Uses JWT token-based authentication.
 * 
 * Environment Variables Required:
 * - REACT_APP_EXTERNAL_API_BASE_URL: Base URL for the external API
 * - REACT_APP_EXTERNAL_CUSTOMER_ID: Customer GUID
 * - REACT_APP_EXTERNAL_API_KEY: API Key for authentication
 * - REACT_APP_EXTERNAL_USER_REF: External user reference (single value for all users)
 */

// Read configuration from environment variables
const EXTERNAL_API_BASE_URL =
  process.env.REACT_APP_EXTERNAL_API_BASE_URL ||
  "https://api.ornekdomain.com/api/external";
const EXTERNAL_CUSTOMER_ID = process.env.REACT_APP_EXTERNAL_CUSTOMER_ID || "";
const EXTERNAL_API_KEY = process.env.REACT_APP_EXTERNAL_API_KEY || "";
const EXTERNAL_USER_REF = process.env.REACT_APP_EXTERNAL_USER_REF || "";

// Create axios instance for external API
const externalApi: AxiosInstance = axios.create({
  baseURL: EXTERNAL_API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token management
let cachedToken: string | null = null;
let tokenExpiresAt: Date | null = null;

/**
 * Check if the current token is valid and not expired
 */
const isTokenValid = (): boolean => {
  if (!cachedToken || !tokenExpiresAt) {
    return false;
  }
  // Check if token expires in next 5 minutes (buffer time)
  const now = new Date();
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  return tokenExpiresAt.getTime() - now.getTime() > bufferTime;
};

/**
 * Get or refresh JWT token
 */
const getAuthToken = async (): Promise<string> => {
  // Return cached token if still valid
  if (isTokenValid() && cachedToken) {
    return cachedToken;
  }

  // Request new token
  const tokenRequest: ExternalAuthTokenRequest = {
    customerId: EXTERNAL_CUSTOMER_ID,
    apiKey: EXTERNAL_API_KEY,
    externalUser: EXTERNAL_USER_REF,
  };

  try {
    const response = await externalApi.post<ExternalAuthTokenResponse>(
      "/auth/token",
      tokenRequest,
    );

    cachedToken = response.data.token;
    tokenExpiresAt = new Date(response.data.expiresAt);

    return cachedToken;
  } catch (error: any) {
    console.error('Auth token request failed:', {
      status: error.response?.status,
      data: error.response?.data,
    });
    throw error;
  }
};

/**
 * Create headers for legacy authentication method
 */
const getLegacyHeaders = (): Record<string, string> => {
  return {
    "X-CustomerId": EXTERNAL_CUSTOMER_ID,
    "X-Api-Key": EXTERNAL_API_KEY,
    "X-External-User": EXTERNAL_USER_REF,
  };
};

export const externalApiService = {
  /**
   * Authenticate and get JWT token
   * @returns JWT token response
   */
  authenticate: async (): Promise<ExternalAuthTokenResponse> => {
    const tokenRequest: ExternalAuthTokenRequest = {
      customerId: EXTERNAL_CUSTOMER_ID,
      apiKey: EXTERNAL_API_KEY,
      externalUser: EXTERNAL_USER_REF,
    };

    const response = await externalApi.post<ExternalAuthTokenResponse>(
      "/auth/token",
      tokenRequest,
    );

    // Cache the token
    cachedToken = response.data.token;
    tokenExpiresAt = new Date(response.data.expiresAt);

    return response.data;
  },

  /**
   * Get list of available forms using JWT token
   * @returns List of available forms
   */
  getForms: async (): Promise<ExternalForm[]> => {
    const response = await externalApi.get<ExternalForm[]>("/forms", {
      headers: getLegacyHeaders(),
    });
    return response.data;
  },

  /**
   * Get list of available forms using legacy headers
   * @returns List of available forms
   */
  getFormsWithHeaders: async (): Promise<ExternalForm[]> => {
    const response = await externalApi.get<ExternalForm[]>("/forms", {
      headers: getLegacyHeaders(),
    });
    return response.data;
  },

  /**
   * Submit a form with data using JWT token
   * @param formId Form ID to submit
   * @param formData Form data (key-value pairs where key is field GUID)
   * @param files Optional files to upload (key is field GUID or file_{GUID})
   * @returns Submission response
   */
  submitForm: async (
    formId: string,
    formData: Record<string, string>,
    files?: Record<string, File>,
  ): Promise<ExternalFormSubmitResponse> => {
    const multipartData = new FormData();

    // Add text fields
    Object.entries(formData).forEach(([key, value]) => {
      multipartData.append(key, value);
    });

    // Add files
    if (files) {
      Object.entries(files).forEach(([key, file]) => {
        multipartData.append(key, file);
      });
    }

    const response = await externalApi.post<ExternalFormSubmitResponse>(
      `/forms/${formId}/submit`,
      multipartData,
      {
        headers: {
          ...getLegacyHeaders(),
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  },

  /**
   * Submit a form with data using legacy headers
   * @param formId Form ID to submit
   * @param formData Form data (key-value pairs where key is field GUID)
   * @param files Optional files to upload (key is field GUID or file_{GUID})
   * @returns Submission response
   */
  submitFormWithHeaders: async (
    formId: string,
    formData: Record<string, string>,
    files?: Record<string, File>,
  ): Promise<ExternalFormSubmitResponse> => {
    const multipartData = new FormData();

    // Add text fields
    Object.entries(formData).forEach(([key, value]) => {
      multipartData.append(key, value);
    });

    // Add files
    if (files) {
      Object.entries(files).forEach(([key, file]) => {
        multipartData.append(key, file);
      });
    }

    const response = await externalApi.post<ExternalFormSubmitResponse>(
      `/forms/${formId}/submit`,
      multipartData,
      {
        headers: {
          ...getLegacyHeaders(),
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  },

  /**
   * Get list of submissions using JWT token
   * @returns List of submissions
   */
  getSubmissions: async (): Promise<ExternalSubmissionListItem[]> => {
    const response = await externalApi.get<ExternalSubmissionListItem[]>(
      "/submissions",
      {
        headers: getLegacyHeaders(),
      },
    );
    return response.data;
  },

  /**
   * Get list of submissions using legacy headers
   * @returns List of submissions
   */
  getSubmissionsWithHeaders: async (): Promise<
    ExternalSubmissionListItem[]
  > => {
    const response = await externalApi.get<ExternalSubmissionListItem[]>(
      "/submissions",
      {
        headers: getLegacyHeaders(),
      },
    );
    return response.data;
  },

  /**
   * Get details of a specific submission using JWT token
   * @param submissionId Submission ID
   * @returns Submission details
   */
  getSubmissionDetail: async (
    submissionId: string,
  ): Promise<ExternalSubmissionDetail> => {
    const response = await externalApi.get<ExternalSubmissionDetail>(
      `/submissions/${submissionId}`,
      {
        headers: getLegacyHeaders(),
      },
    );
    return response.data;
  },

  /**
   * Get details of a specific submission using legacy headers
   * @param submissionId Submission ID
   * @returns Submission details
   */
  getSubmissionDetailWithHeaders: async (
    submissionId: string,
  ): Promise<ExternalSubmissionDetail> => {
    const response = await externalApi.get<ExternalSubmissionDetail>(
      `/submissions/${submissionId}`,
      {
        headers: getLegacyHeaders(),
      },
    );
    return response.data;
  },

  /**
   * Clear cached token (useful for logout or forcing re-authentication)
   */
  clearToken: () => {
    cachedToken = null;
    tokenExpiresAt = null;
  },

  /**
   * Get current configuration (useful for debugging)
   */
  getConfig: () => ({
    baseURL: EXTERNAL_API_BASE_URL,
    hasCustomerId: !!EXTERNAL_CUSTOMER_ID,
    hasApiKey: !!EXTERNAL_API_KEY,
    hasExternalUserRef: !!EXTERNAL_USER_REF,
    externalUserRef: EXTERNAL_USER_REF,
  }),
};

export default externalApiService;
